import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { COLORS } from '../marketingLeadConstants';
import { normalizeToE164 } from '../utils/marketingLeadHelpers';

export const CALL_STATUS_CONFIG = {
  idle:        { dot: '#9CA3AF', label: 'Initializing',     pulse: false },
  registering: { dot: '#F59E0B', label: 'Setting up…',      pulse: true  },
  ready:       { dot: '#10B981', label: 'Ready to call',    pulse: false },
  calling:     { dot: '#3B82F6', label: 'Connecting…',      pulse: true  },
  in_call:     { dot: '#10B981', label: 'In call',          pulse: true  },
  ended:       { dot: '#9CA3AF', label: 'Call ended',       pulse: false },
  error:       { dot: '#EF4444', label: 'Device error',     pulse: false },
};

const CALL_OUTCOME_OPTIONS = [
  { value: 'reached_restaurant',    label: 'Reached restaurant' },
  { value: 'meeting_scheduled',     label: 'Meeting scheduled' },
  { value: 'user_not_available',    label: 'User not available' },
  { value: 'connected_to_voicemail',label: 'Connected to voicemail' },
  { value: 'did_not_connect',       label: 'Did not connect' },
  { value: 'number_disconnected',   label: 'Number disconnected' },
  { value: 'wrong_number',          label: 'Wrong number' },
];

function CallTimer() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function CallSmsModal({ modal, voice, onClose, onSmsConfirm, onSaveCallOutcome }) {
  const { open, type, row } = modal;

  // SMS inline section
  const [smsOpen, setSmsOpen]           = useState(false);
  const [smsConfirming, setSmsConfirming] = useState(false);

  // Call outcome section (shown after call ends)
  const [callOutcome, setCallOutcome]     = useState('');
  const [callNotes, setCallNotes]         = useState('');
  const [meetingDateTime, setMeetingDateTime] = useState(null);
  const [meetingDateTimeError, setMeetingDateTimeError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const [outcomeSaved, setOutcomeSaved]   = useState(false);

  // Latched flag — once the call ends it stays true until modal closes
  const [callEnded, setCallEnded] = useState(false);

  // Reset everything when modal closes
  useEffect(() => {
    if (!open) {
      setSmsOpen(false);
      setSmsConfirming(false);
      setCallOutcome('');
      setCallNotes('');
      setMeetingDateTime(null);
      setMeetingDateTimeError('');
      setPickerOpen(false);
      setOutcomeSaving(false);
      setOutcomeSaved(false);
      setCallEnded(false);
    }
  }, [open]);

  // Latch callEnded as soon as voice status hits 'ended' — never revert
  useEffect(() => {
    if (voice.status === 'ended' && type === 'call') {
      setCallEnded(true);
      setOutcomeSaved(false);
      // Close SMS section when call ends — outcome form takes over
      setSmsOpen(false);
      setSmsConfirming(false);
    }
  }, [voice.status, type]);

  const isCallActive = voice.is_calling || voice.is_in_call;
  const isCallEnded  = callEnded;

  const handleClose = () => {
    setSmsOpen(false);
    setSmsConfirming(false);
    onClose();
  };

  const handleSmsOpen        = () => { setSmsOpen(true); setSmsConfirming(false); };
  const handleSmsClose       = () => { setSmsOpen(false); setSmsConfirming(false); };
  const handleSmsConfirmStep = () => setSmsConfirming(true);
  const handleSmsSend        = () => { setSmsOpen(false); setSmsConfirming(false); onSmsConfirm(row); };

  const handleOutcomeSave = async () => {
    if (!callOutcome) return;
    if (callOutcome === 'meeting_scheduled') {
      if (!meetingDateTime || !meetingDateTime.isValid()) {
        setMeetingDateTimeError('Please select a meeting date and time.');
        return;
      }
      setMeetingDateTimeError('');
    }
    setOutcomeSaving(true);
    await onSaveCallOutcome(row, {
      outcome: callOutcome,
      notes: callNotes,
      ...(callOutcome === 'meeting_scheduled' && { meetingAt: meetingDateTime.toISOString() }),
    });
    setOutcomeSaving(false);
    setOutcomeSaved(true);
  };

  // ── Standalone SMS modal (opened from table row SMS button) ──────────────────
  if (type === 'sms') {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth maxWidth='xs'
        PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E8E8E8', boxShadow: '0 8px 32px rgba(0,0,0,0.10)' } }}
      >
        <DialogTitle sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 16, color: '#111111', borderBottom: '1px solid #F0F0F0', pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
            <SmsRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          Send SMS to Lead
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ p: 2, borderRadius: '10px', bgcolor: '#FAFAFA', border: '1px solid #F0F0F0' }}>
            <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, color: '#111111', fontSize: 15, mb: 1 }}>{row?.name || '—'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <PhoneInTalkRoundedIcon sx={{ fontSize: 14, color: '#374151' }} />
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 14, color: '#374151' }}>{row?.phone || '—'}</Typography>
            </Box>
            <Typography variant='caption' sx={{ color: COLORS.muted, fontFamily: 'Inter Regular, sans-serif', display: 'block' }}>{row?.address || '—'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.75, borderRadius: '10px', bgcolor: '#FFFBEB', border: '1.5px solid #FCD34D' }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#D97706', flexShrink: 0, mt: 0.1 }} />
            <Box>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13, color: '#92400E', mb: 0.4 }}>Only send SMS after calling the restaurant</Typography>
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#B45309', lineHeight: 1.55 }}>
                Sending unsolicited SMS without prior contact may get your number flagged as spam and permanently blocked by carriers. Always call first.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#8C8C8C', fontFamily: 'Inter Regular, sans-serif', borderRadius: '8px' }}>Cancel</Button>
          <Button onClick={() => onSmsConfirm(row)} variant='contained' startIcon={<SmsRoundedIcon sx={{ fontSize: 15 }} />}
            sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, borderRadius: '8px', bgcolor: '#111111', boxShadow: 'none', px: 2.5, '&:hover': { bgcolor: '#333333', boxShadow: 'none' } }}>
            Confirm Send
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Modal is locked when call is active OR when call ended but outcome not yet saved
  const isLocked = isCallActive || (isCallEnded && !outcomeSaved);

  // ── Call modal ───────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={isLocked}
      onClose={(_, reason) => {
        if (isLocked) return;
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      fullWidth maxWidth='xs'
      PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E8E8E8', boxShadow: '0 8px 32px rgba(0,0,0,0.10)' } }}
    >
      {/* ── Title ── */}
      <DialogTitle sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 16, color: '#111111', borderBottom: '1px solid #F0F0F0', pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
          <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
        Call Lead
        {/* Lock indicator while call is active */}
        {isCallActive && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, borderRadius: '20px', bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#DC2626', '@keyframes lp': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }, animation: 'lp 1.2s ease-in-out infinite' }} />
            <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 11, fontWeight: 700, color: '#DC2626' }}>Live</Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>

        {/* Device status row */}
        {(() => {
          const cfg = CALL_STATUS_CONFIG[voice.status] || CALL_STATUS_CONFIG.idle;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5, px: 1.5, py: 1.25, bgcolor: '#FAFAFA', border: '1px solid #F0F0F0', borderRadius: '10px' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0, ...(cfg.pulse && { '@keyframes tw_pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } }, animation: 'tw_pulse 1.2s ease-in-out infinite' }) }} />
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#374151', flex: 1 }}>{cfg.label}</Typography>
              {voice.is_in_call && (
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#111111' }}><CallTimer /></Typography>
              )}
            </Box>
          );
        })()}

        {/* Lead info card */}
        <Box sx={{ p: 2, borderRadius: '10px', bgcolor: '#FAFAFA', border: '1px solid #F0F0F0' }}>
          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, color: '#111111', fontSize: 15, mb: 1 }}>{row?.name || '—'}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
            <PhoneInTalkRoundedIcon sx={{ fontSize: 14, color: '#374151' }} />
            <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 14, color: '#374151' }}>{row?.phone || '—'}</Typography>
            {row?.phone && !normalizeToE164(row.phone) && (
              <Typography sx={{ fontSize: 11, color: '#EF4444', fontFamily: 'Inter Regular, sans-serif' }}>(unrecognized format)</Typography>
            )}
          </Box>
          <Typography variant='caption' sx={{ color: COLORS.muted, fontFamily: 'Inter Regular, sans-serif', display: 'block' }}>{row?.address || '—'}</Typography>
        </Box>

        {/* No phone warning */}
        {!row?.phone && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FEF9C3', border: '1px solid #FDE047', borderRadius: '10px' }}>
            <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#854D0E' }}>No phone number on file for this lead.</Typography>
          </Box>
        )}

        {/* Twilio error banner */}
        {voice.last_error && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CancelRounded sx={{ fontSize: 15, color: '#DC2626', flexShrink: 0, mt: 0.15 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#DC2626' }}>{voice.last_error}</Typography>
              {voice.status === 'error' && (
                <Button size='small' onClick={voice.retry_init} sx={{ mt: 0.75, textTransform: 'none', fontSize: 12, p: 0, color: '#111111', fontFamily: 'Inter Bold, sans-serif', '&:hover': { background: 'none', textDecoration: 'underline' } }}>
                  Retry connection
                </Button>
              )}
            </Box>
            <IconButton size='small' onClick={voice.clear_error} sx={{ p: 0.25, color: '#DC2626' }}>
              <CancelRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}

        {/* ── Call outcome section — appears after call ends ── */}
        <Collapse in={isCallEnded} timeout={260} unmountOnExit>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Section label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '20px', bgcolor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
                <NoteAltOutlinedIcon sx={{ fontSize: 11, color: '#6B7280' }} />
                <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 11, color: '#6B7280', fontWeight: 700 }}>Call Outcome</Typography>
              </Box>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
            </Box>

            {outcomeSaved ? (
              /* ── Saved confirmation ── */
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: '10px', bgcolor: '#F0FDF4', border: '1.5px solid #86EFAC' }}>
                <CheckRoundedIcon sx={{ fontSize: 17, color: '#16A34A', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13, color: '#15803D' }}>Outcome saved successfully.</Typography>
              </Box>
            ) : (
              <>
                {/* Outcome dropdown */}
                <FormControl size='small' fullWidth>
                  <InputLabel sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13 }}>Call outcome</InputLabel>
                  <Select
                    label='Call outcome'
                    value={callOutcome}
                    onChange={(e) => {
                      setCallOutcome(e.target.value);
                      setMeetingDateTime(null);
                      setMeetingDateTimeError('');
                    }}
                    sx={{ borderRadius: '9px', fontFamily: 'Inter Regular, sans-serif', fontSize: 13 }}
                  >
                    {CALL_OUTCOME_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Meeting date & time picker — shown only for meeting_scheduled */}
                <Collapse in={callOutcome === 'meeting_scheduled'} timeout={220} unmountOnExit>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.5, borderRadius: '10px', bgcolor: '#F5F3FF', border: '1.5px solid #C4B5FD' }}>

                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: '#6366F1' }} />
                      <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 12.5, color: '#4338CA' }}>
                        Schedule Meeting
                      </Typography>
                    </Box>

                    {/* Timezone reminder */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.6, borderRadius: '7px', bgcolor: '#EDE9FE', border: '1px solid #C4B5FD' }}>
                      <WarningAmberRoundedIcon sx={{ fontSize: 13, color: '#7C3AED', flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#5B21B6', lineHeight: 1.5 }}>
                        You are in India (IST) — enter the date &amp; time in the <strong>restaurant's local US time</strong>.
                      </Typography>
                    </Box>

                    {/* Date & time picker — allows from yesterday onward, opens on focus */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label='Meeting date & time'
                        value={meetingDateTime}
                        open={pickerOpen}
                        onOpen={() => setPickerOpen(true)}
                        onClose={() => setPickerOpen(false)}
                        onChange={(newVal) => {
                          setMeetingDateTime(newVal);
                          setMeetingDateTimeError('');
                        }}
                        minDate={dayjs().subtract(1, 'day')}
                        minutesStep={5}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !!meetingDateTimeError,
                            helperText: meetingDateTimeError || "Enter time in the restaurant's US timezone",
                            onFocus: () => setPickerOpen(true),
                            sx: {
                              bgcolor: '#fff', borderRadius: '9px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '9px',
                                fontFamily: 'Inter Regular, sans-serif',
                                fontSize: 13,
                                '& fieldset': { borderColor: meetingDateTimeError ? '#DC2626' : '#C4B5FD' },
                                '&:hover fieldset': { borderColor: meetingDateTimeError ? '#DC2626' : '#A78BFA' },
                                '&.Mui-focused fieldset': { borderColor: meetingDateTimeError ? '#DC2626' : '#6366F1', borderWidth: '1px' },
                              },
                              '& .MuiInputLabel-root': { fontFamily: 'Inter Regular, sans-serif', fontSize: 13 },
                              '& .MuiFormHelperText-root': {
                                fontFamily: 'Inter Regular, sans-serif',
                                fontSize: 11,
                                color: meetingDateTimeError ? '#DC2626' : '#7C3AED',
                                mt: 0.5,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Collapse>

                {/* Notes input */}
                <TextField
                  size='small'
                  label='Notes (optional)'
                  placeholder='Add any notes about this call…'
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '9px', fontFamily: 'Inter Regular, sans-serif', fontSize: 13,
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#B0B8CC' },
                      '&.Mui-focused fieldset': { borderColor: '#111111', borderWidth: '1px' },
                    },
                    '& .MuiInputLabel-root': { fontFamily: 'Inter Regular, sans-serif', fontSize: 13 },
                  }}
                />

                {/* Save outcome button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size='small'
                    disabled={
                      !callOutcome ||
                      outcomeSaving ||
                      (callOutcome === 'meeting_scheduled' && (!meetingDateTime || !meetingDateTime.isValid()))
                    }
                    onClick={handleOutcomeSave}
                    variant='contained'
                    startIcon={outcomeSaving
                      ? <CircularProgress size={12} sx={{ color: '#8C8C8C' }} />
                      : <CheckCircleOutlineRoundedIcon sx={{ fontSize: 14 }} />
                    }
                    sx={{
                      textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700,
                      fontSize: 12.5, borderRadius: '8px', bgcolor: '#111111',
                      boxShadow: 'none', px: 2,
                      '&:hover': { bgcolor: '#333333', boxShadow: 'none' },
                      '&.Mui-disabled': { bgcolor: '#E8E8E8', color: '#9CA3AF', boxShadow: 'none' },
                    }}
                  >
                    {outcomeSaving ? 'Saving…' : 'Save Outcome'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Collapse>

        {/* ── Inline SMS section — always available, call keeps running ── */}
        <Collapse in={smsOpen} timeout={220} unmountOnExit>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Divider label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#F0F0F0' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '20px', bgcolor: '#FEF3C7', border: '1px solid #FCD34D' }}>
                <SmsRoundedIcon sx={{ fontSize: 11, color: '#D97706' }} />
                <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 11, color: '#D97706', fontWeight: 700 }}>Send SMS</Typography>
              </Box>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#F0F0F0' }} />
            </Box>

            {/* Spam warning */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.5, borderRadius: '10px', bgcolor: '#FFFBEB', border: '1.5px solid #FCD34D' }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 17, color: '#D97706', flexShrink: 0, mt: 0.1 }} />
              <Box>
                <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 12.5, color: '#92400E', mb: 0.3 }}>
                  Only send SMS after contacting the restaurant
                </Typography>
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#B45309', lineHeight: 1.55 }}>
                  Sending unsolicited SMS without prior contact may get your number flagged as spam and permanently blocked by carriers.
                </Typography>
              </Box>
            </Box>

            {/* Confirm prompt */}
            <Collapse in={smsConfirming} timeout={180} unmountOnExit>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '10px', bgcolor: '#F0FDF4', border: '1.5px solid #86EFAC' }}>
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 17, color: '#16A34A', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#15803D' }}>
                  Confirmed prior contact. Ready to send.
                </Typography>
              </Box>
            </Collapse>

            {/* SMS action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
              <Button size='small' onClick={handleSmsClose} sx={{ textTransform: 'none', fontSize: 12, color: '#9CA3AF', fontFamily: 'Inter Regular, sans-serif', borderRadius: '7px' }}>
                Cancel
              </Button>
              {smsConfirming ? (
                <Button size='small' onClick={handleSmsSend} variant='contained' startIcon={<SmsRoundedIcon sx={{ fontSize: 13 }} />}
                  sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 12.5, borderRadius: '7px', bgcolor: '#111111', boxShadow: 'none', px: 2, '&:hover': { bgcolor: '#333333', boxShadow: 'none' } }}>
                  Send SMS Now
                </Button>
              ) : (
                <Button size='small' onClick={handleSmsConfirmStep} variant='contained'
                  sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 12.5, borderRadius: '7px', bgcolor: '#D97706', boxShadow: 'none', px: 2, '&:hover': { bgcolor: '#B45309', boxShadow: 'none' } }}>
                  I've called — Confirm
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>

      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>

        {/* Close — hidden while call is active OR outcome not yet saved */}
        {!isLocked && (
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none', color: '#8C8C8C', fontFamily: 'Inter Regular, sans-serif', borderRadius: '8px' }}
          >
            Close
          </Button>
        )}

        {/* Send SMS — only visible while call is active */}
        {isCallActive && !smsOpen && (
          <Button
            onClick={handleSmsOpen}
            startIcon={<SmsRoundedIcon sx={{ fontSize: 15 }} />}
            variant='outlined'
            sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, borderRadius: '8px', borderColor: '#FCD34D', color: '#D97706', bgcolor: '#FFFBEB', boxShadow: 'none', px: 2, '&:hover': { bgcolor: '#FEF3C7', borderColor: '#F59E0B', color: '#B45309' } }}
          >
            Send SMS
          </Button>
        )}

        {voice.is_in_call && (
          <Button
            onClick={voice.toggle_mute}
            variant='outlined'
            sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', borderRadius: '8px', borderColor: voice.is_muted ? '#F59E0B' : '#E8E8E8', color: voice.is_muted ? '#D97706' : '#374151', bgcolor: voice.is_muted ? '#FEF3C7' : 'transparent', '&:hover': { borderColor: '#D97706', bgcolor: '#FEF3C7', color: '#D97706' } }}
          >
            {voice.is_muted ? '🔇 Unmute' : '🎙 Mute'}
          </Button>
        )}

        {voice.is_in_call || voice.is_calling ? (
          <Button
            onClick={voice.hangup}
            variant='contained'
            sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, borderRadius: '8px', bgcolor: '#DC2626', boxShadow: 'none', px: 2.5, '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' } }}
          >
            Hang Up
          </Button>
        ) : (
          !isCallEnded && (
            <Button
              disabled={!voice.is_ready || !normalizeToE164(row?.phone)}
              onClick={() => {
                // const to = normalizeToE164(row?.phone);
                // if (to) voice.call(to);
                console.log('====================================>>>>>>>>>>>>>>>>[MarketingLead][voice.call] call:', '+17739975315');
                voice.call('+17739975315');
              }}
              variant='contained'
              startIcon={<PhoneInTalkRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, borderRadius: '8px', bgcolor: '#111111', boxShadow: 'none', px: 2.5, '&:hover': { bgcolor: '#333333', boxShadow: 'none' }, '&.Mui-disabled': { bgcolor: '#E8E8E8', color: '#8C8C8C', boxShadow: 'none' } }}
            >
              {voice.status === 'registering' ? 'Setting up…' : 'Start Call'}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
}
